import axios from 'axios';
import config from '../config';

// Authentication token management
const getAuthToken = () => localStorage.getItem('token');
const setAuthToken = (token) => localStorage.setItem('token', token);
const removeAuthToken = () => localStorage.removeItem('token');

// Create an axios instance with the base URL from config
const apiClient = axios.create({
    baseURL: config.api.baseUrl,
    headers: {
        'Content-Type': 'application/json',
    }
});

// Add a request interceptor to include the auth token in all requests
apiClient.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Add a response interceptor to handle 401 Unauthorized errors
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            // If we get a 401, clear the token and redirect to login
            removeAuthToken();
            // Don't reload the page, let the React components handle the state change
            // This prevents infinite loops of API calls
        }
        return Promise.reject(error);
    }
);

// API service for all backend requests
const apiService = {
    // Utility methods
    getApiBaseUrl: () => config.api.baseUrl,

    // Authentication
    login: (username, password) => {
        // FastAPI's OAuth2PasswordRequestForm expects x-www-form-urlencoded format
        const params = new URLSearchParams();
        params.append('username', username);
        params.append('password', password);

        return axios.post(`${config.api.baseUrl}/token`, params, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        })
            .then(response => {
                console.log('Login response:', response.data);
                // Save the token
                setAuthToken(response.data.access_token);
                return response;
            })
            .catch(error => {
                console.error('Login error details:', error.response ? error.response.data : error.message);
                throw error;
            });
    },

    logout: () => {
        removeAuthToken();
    },

    isAuthenticated: () => {
        return !!getAuthToken();
    },

    // Brands
    getBrands: (country = null, year = null) => {
        const params = {};
        if (country) params.country = country;
        if (year) params.year = year;
        return apiClient.get('/brands', { params });
    },

    // Categories
    getCategories: (country = null, year = null) => {
        const params = {};
        if (country) params.country = country;
        if (year) params.year = year;
        return apiClient.get('/categories', { params });
    },
    getBrandCategories: (brandName, country = null, year = null) => {
        const token = getAuthToken();
        const formData = new FormData();
        formData.append('brand_name', brandName);
        if (country) {
            formData.append('country', country);
        }
        if (year) {
            formData.append('year', year);
        }
        return axios.post(`${config.api.baseUrl}/brand/categories`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': token ? `Bearer ${token}` : '',
            },
        });
    },

    // Countries
    get: (endpoint) => apiClient.get(endpoint),

    // Rules the report checker applies (read-only for now)
    getLinterRules: () => apiClient.get('/linter-rules'),

    // Testimonials (ranked open-ended responses)
    getTestimonials: ({ country = null, year = null, brand = null, category = null, order = 'longest' } = {}) => {
        const params = { order };
        if (country) params.country = country;
        if (year) params.year = year;
        if (brand) params.brand = brand;
        if (category) params.category = category;
        return apiClient.get('/testimonials', { params });
    },

    // Download ranked testimonials as a CSV file
    exportTestimonials: async ({ country = null, year = null, brand = null, category = null, order = 'longest' } = {}) => {
        const params = { order };
        if (country) params.country = country;
        if (year) params.year = year;
        if (brand) params.brand = brand;
        if (category) params.category = category;

        const response = await apiClient.get('/testimonials/export', {
            params,
            responseType: 'blob',
        });

        // Derive a filename from the Content-Disposition header when present
        let filename = 'testimonials.csv';
        const disposition = response.headers['content-disposition'];
        if (disposition) {
            const match = disposition.match(/filename="?([^"]+)"?/);
            if (match) filename = match[1];
        }

        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return filename;
    },

    // Brand data
    getBrandData: (brandName, country = null) => {
        const token = getAuthToken();
        const formData = new FormData();
        formData.append('brand_name', brandName);
        if (country) {
            formData.append('country', country);
        }
        return axios.post(`${config.api.baseUrl}/brand/data`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': token ? `Bearer ${token}` : '',
            },
        });
    },

    // Report generation
    generateReport: (formData) => {
        const token = getAuthToken();
        return axios.post(`${config.api.baseUrl}/generate-report`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': token ? `Bearer ${token}` : '',
            },
        });
    },

    // Get report generation progress
    getReportProgress: (requestId) => {
        const token = getAuthToken();
        return axios.get(`${config.api.baseUrl}/report-progress/${requestId}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
            },
        });
    },

    // Chat functionality
    initializeChat: (reportData = null, country = null, year = 2025, reportSessionId = null) => {
        const token = getAuthToken();
        const formData = new FormData();
        if (reportData) {
            formData.append('report', JSON.stringify(reportData));
        }
        if (country) {
            formData.append('country', country);
        }
        if (year) {
            formData.append('year', year);
        }
        // Link the chat to the saved report so live edits persist back to it
        if (reportSessionId) {
            formData.append('report_session_id', reportSessionId);
        }
        return axios.post(`${config.api.baseUrl}/initialize-chat`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': token ? `Bearer ${token}` : '',
            },
        });
    },

    sendChatMessage: (sessionId, message, country = null) => {
        const token = getAuthToken();
        const formData = new FormData();
        formData.append('session_id', sessionId);
        formData.append('message', message);
        if (country) {
            formData.append('country', country);
        }
        return axios.post(`${config.api.baseUrl}/chat-message`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': token ? `Bearer ${token}` : '',
            },
        });
    },

    // Chat session management
    getChatSessions: () => {
        return apiClient.get('/chat-sessions');
    },

    getChatSessionMessages: (sessionId) => {
        return apiClient.get(`/chat-session/${sessionId}/messages`);
    },

    // Report session management
    getReportSessions: () => {
        return apiClient.get('/report-sessions');
    },

    getReportSession: (sessionId) => {
        return apiClient.get(`/report-session/${sessionId}`);
    },

    // Ask the LLM to fix the data-vs-claims linter findings on a saved report.
    // `findings` is the subset of warnings the user chose to keep (they can
    // ignore individual ones); when omitted, every finding is fixed.
    // Returns the updated report session (with corrected prose + refreshed lint).
    fixReportLint: (sessionId, findings) => {
        const body = findings ? { findings } : {};
        return apiClient.post(`/fix-report-lint/${sessionId}`, body);
    },

    // Shared reports (no authentication required)
    getSharedReport: (sessionId) => {
        // Create a direct axios request without authentication interceptors
        return axios.get(`${config.api.baseUrl}/share/report/${sessionId}`);
    },

    // Shared chats (no authentication required)
    getSharedChat: (sessionId) => {
        // Create a direct axios request without authentication interceptors
        return axios.get(`${config.api.baseUrl}/share/chat/${sessionId}`);
    },
};

export default apiService;
